import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, CreditCard, Gift, Users, Leaf, Sun, CheckCircle, Loader2, Wallet, Shield, ArrowRight, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../config/api';
import axios from 'axios';

const Donate = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donorInfo, setDonorInfo] = useState({
    name: '',
    email: '',
    anonymous: false
  });
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  // Crypto payment state
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe', 'crypto', 'moncash', 'natcash'
  const [selectedCrypto, setSelectedCrypto] = useState('usdttrc20');
  const [availableCryptos, setAvailableCryptos] = useState([]);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [cryptoPayment, setCryptoPayment] = useState(null);
  const [cryptoStatus, setCryptoStatus] = useState('waiting');
  const [copied, setCopied] = useState(false);
  
  // Mobile payment state (Moncash/Natcash)
  const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(false);
  const [mobilePaymentCopied, setMobilePaymentCopied] = useState(false);
  const [mobilePaymentSubmitting, setMobilePaymentSubmitting] = useState(false);
  const [mobilePaymentConfirmed, setMobilePaymentConfirmed] = useState(false);
  
  // Payment contact info
  const MONCASH_NUMBER = '+509-4460-0357';
  const NATCASH_NUMBER = '+509-5608-8028';
  const WHATSAPP_NUMBER = '50939451425'; // Haiti Digi number for WhatsApp contact
  const PAYPAL_EMAIL = 'marathonedenlife@gmail.com';
  const INTERAC_EMAIL = 'marathonedenlife@gmail.com';

  // Get payment info based on method
  const getPaymentInfo = () => {
    switch(paymentMethod) {
      case 'moncash':
        return { type: 'phone', value: MONCASH_NUMBER, label: 'Moncash', color: 'from-yellow-500 to-orange-500' };
      case 'natcash':
        return { type: 'phone', value: NATCASH_NUMBER, label: 'Natcash', color: 'from-blue-500 to-blue-600' };
      case 'paypal':
        return { type: 'email', value: PAYPAL_EMAIL, label: 'PayPal', color: 'from-blue-600 to-blue-800' };
      case 'interac':
        return { type: 'email', value: INTERAC_EMAIL, label: 'Virement Interac', color: 'from-yellow-500 to-yellow-600' };
      default:
        return { type: 'phone', value: '', label: '', color: 'from-gray-500 to-gray-600' };
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text.replace(/-/g, ''));
    setMobilePaymentCopied(true);
    setTimeout(() => setMobilePaymentCopied(false), 2000);
    toast.success('Copié! ✓');
  };

  // Function to submit mobile payment request for donations
  const submitMobilePaymentRequest = async () => {
    if (!donorInfo.email) {
      toast.error('Veuillez entrer votre email');
      return;
    }
    
    setMobilePaymentSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/mobile-payment/request`, {
        payment_type: 'donation',
        payment_method: paymentMethod,
        amount: getFinalAmount(),
        quantity: null,
        email: donorInfo.email,
        name: donorInfo.anonymous ? null : donorInfo.name,
        affiliate_code: null,
        language: i18n.language
      });
      
      if (response.data.success) {
        setMobilePaymentConfirmed(true);
        toast.success('✅ Demande enregistrée ! Un email de confirmation vous a été envoyé.');
      }
    } catch (error) {
      console.error('Mobile payment request error:', error);
      toast.error('Erreur lors de l\'enregistrement de la demande');
    } finally {
      setMobilePaymentSubmitting(false);
    }
  };

  // Fetch available cryptocurrencies
  useEffect(() => {
    const fetchCryptos = async () => {
      try {
        const response = await axios.get(`${API_URL}/crypto/currencies`);
        setAvailableCryptos(response.data.currencies || []);
      } catch (error) {
        console.error('Failed to fetch cryptos:', error);
        setAvailableCryptos([
          { code: 'usdttrc20', name: 'USDT (TRC20)' },
          { code: 'btc', name: 'Bitcoin (BTC)' },
          { code: 'eth', name: 'Ethereum (ETH)' }
        ]);
      }
    };
    fetchCryptos();
  }, []);

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    const payment = searchParams.get('payment');
    const donationId = searchParams.get('donation_id');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && donationId) {
      toast.success(content[i18n.language]?.successMessage || content.fr.successMessage);
      navigate('/donate', { replace: true });
    } else if (canceled === 'true') {
      toast.error(content[i18n.language]?.canceledMessage || content.fr.canceledMessage);
      navigate('/donate', { replace: true });
    } else if (payment === 'success') {
      toast.success(content[i18n.language]?.successMessage || content.fr.successMessage);
      navigate('/donate', { replace: true });
    } else if (sessionId) {
      checkDonationStatus(sessionId);
    }
  }, [searchParams, navigate, i18n.language]);

  // Pre-fill user info if logged in
  useEffect(() => {
    if (user && !donorInfo.anonymous) {
      setDonorInfo(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || ''
      }));
    }
  }, [user]);

  const checkDonationStatus = async (sessionId) => {
    setCheckingStatus(true);
    try {
      const response = await fetch(`${API_URL}/donations/status/${sessionId}`);
      const data = await response.json();
      
      if (data.status === 'completed') {
        toast.success(content[i18n.language]?.successMessage || content.fr.successMessage);
        navigate('/donate', { replace: true });
      }
    } catch (error) {
      console.error('Error checking donation status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const content = {
    fr: {
      title: "Faire un Don",
      subtitle: "Soutenez le développement durable en Haïti",
      intro: "Votre générosité fait la différence. Chaque don contribue directement à nos projets d'énergie renouvelable, d'agriculture durable et d'entrepreneuriat en Haïti.",
      impactTitle: "L'Impact de Votre Don",
      impacts: [
        { icon: Sun, amount: "50 USD", desc: "Fournit un kit solaire pour une famille" },
        { icon: Leaf, amount: "100 USD", desc: "Plante 50 arbres fruitiers" },
        { icon: Users, amount: "250 USD", desc: "Formation entrepreneuriale pour 5 jeunes" },
        { icon: Gift, amount: "500 USD", desc: "Équipement agricole pour un agriculteur" }
      ],
      selectAmount: "Sélectionnez un montant",
      amounts: [25, 50, 100, 250, 500, 1000],
      customAmount: "Autre montant",
      customPlaceholder: "Entrez votre montant",
      donorInfo: "Vos informations",
      nameLabel: "Votre nom",
      namePlaceholder: "Jean Dupont",
      emailLabel: "Email (pour reçu fiscal)",
      emailPlaceholder: "votre@email.com",
      anonymous: "Faire un don anonyme",
      useAccount: "Utiliser mon compte",
      totalDonation: "Votre don",
      donateBtn: "Faire un don de",
      processing: "Redirection vers le paiement...",
      secureNote: "Paiement 100% sécurisé",
      paymentMethods: "Méthodes de paiement acceptées",
      taxNote: "Un reçu fiscal vous sera envoyé par email",
      thankYou: "Merci pour votre générosité !",
      whyDonate: "Pourquoi Donner ?",
      reasons: [
        "100% des dons vont directement aux projets en Haïti",
        "Organisation à but non lucratif enregistrée",
        "Rapports d'impact transparents",
        "Reçu fiscal disponible"
      ],
      contactNote: "Questions sur les dons ?",
      successMessage: "Merci pour votre don ! Vous recevrez un reçu par email.",
      canceledMessage: "Don annulé. Vous pouvez réessayer quand vous le souhaitez.",
      minAmountError: "Le montant minimum est de 5 USD",
      loginPrompt: "Connectez-vous pour utiliser vos informations",
      orContinueAs: "ou continuez comme invité"
    },
    en: {
      title: "Make a Donation",
      subtitle: "Support sustainable development in Haiti",
      intro: "Your generosity makes a difference. Every donation directly contributes to our renewable energy, sustainable agriculture and entrepreneurship projects in Haiti.",
      impactTitle: "Your Donation Impact",
      impacts: [
        { icon: Sun, amount: "$50 USD", desc: "Provides a solar kit for a family" },
        { icon: Leaf, amount: "$100 USD", desc: "Plants 50 fruit trees" },
        { icon: Users, amount: "$250 USD", desc: "Entrepreneurship training for 5 youth" },
        { icon: Gift, amount: "$500 USD", desc: "Agricultural equipment for a farmer" }
      ],
      selectAmount: "Select an amount",
      amounts: [25, 50, 100, 250, 500, 1000],
      customAmount: "Other amount",
      customPlaceholder: "Enter your amount",
      donorInfo: "Your information",
      nameLabel: "Your name",
      namePlaceholder: "John Doe",
      emailLabel: "Email (for tax receipt)",
      emailPlaceholder: "your@email.com",
      anonymous: "Make an anonymous donation",
      useAccount: "Use my account",
      totalDonation: "Your donation",
      donateBtn: "Donate",
      processing: "Redirecting to payment...",
      secureNote: "100% secure payment",
      paymentMethods: "Accepted payment methods",
      taxNote: "A tax receipt will be sent to your email",
      thankYou: "Thank you for your generosity!",
      whyDonate: "Why Donate?",
      reasons: [
        "100% of donations go directly to projects in Haiti",
        "Registered non-profit organization",
        "Transparent impact reports",
        "Tax receipt available"
      ],
      contactNote: "Questions about donations?",
      successMessage: "Thank you for your donation! You will receive a receipt by email.",
      canceledMessage: "Donation canceled. You can try again whenever you want.",
      minAmountError: "Minimum amount is $5 USD",
      loginPrompt: "Log in to use your information",
      orContinueAs: "or continue as guest"
    },
    ht: {
      title: "Fè yon Don",
      subtitle: "Sipòte devlopman dirab an Ayiti",
      intro: "Jenerozite ou fè yon diferans. Chak don kontribye dirèkteman nan pwojè enèji renouvlab, agrikilti dirab ak antreprenarya nou yo an Ayiti.",
      impactTitle: "Enpak Don Ou",
      impacts: [
        { icon: Sun, amount: "50 USD", desc: "Bay yon kit solè pou yon fanmi" },
        { icon: Leaf, amount: "100 USD", desc: "Plante 50 pyebwa fwi" },
        { icon: Users, amount: "250 USD", desc: "Fòmasyon antreprenarya pou 5 jèn" },
        { icon: Gift, amount: "500 USD", desc: "Ekipman agrikòl pou yon kiltivatè" }
      ],
      selectAmount: "Chwazi yon montan",
      amounts: [25, 50, 100, 250, 500, 1000],
      customAmount: "Lòt montan",
      customPlaceholder: "Antre montan ou",
      donorInfo: "Enfòmasyon ou",
      nameLabel: "Non ou",
      namePlaceholder: "Jan Batis",
      emailLabel: "Imel (pou resi fiskal)",
      emailPlaceholder: "imel@ou.com",
      anonymous: "Fè yon don anonim",
      useAccount: "Itilize kont mwen",
      totalDonation: "Don ou",
      donateBtn: "Fè don",
      processing: "Ap voye nan peman...",
      secureNote: "Peman 100% sekirize",
      paymentMethods: "Metòd peman aksepte",
      taxNote: "Yon resi fiskal ap voye nan imel ou",
      thankYou: "Mèsi pou jenerozite ou!",
      whyDonate: "Poukisa Bay?",
      reasons: [
        "100% don yo ale dirèkteman nan pwojè an Ayiti",
        "Òganizasyon san bi likratif anrejistre",
        "Rapò enpak transparan",
        "Resi fiskal disponib"
      ],
      contactNote: "Kesyon sou don yo?",
      successMessage: "Mèsi pou don ou! Ou ap resevwa yon resi pa imel.",
      canceledMessage: "Don anile. Ou ka eseye ankò lè ou vle.",
      minAmountError: "Montan minimòm se 5 USD",
      loginPrompt: "Konekte pou itilize enfòmasyon ou",
      orContinueAs: "oswa kontinye kòm envite"
    },
    es: {
      title: "Hacer una Donación",
      subtitle: "Apoya el desarrollo sostenible en Haití",
      intro: "Tu generosidad hace la diferencia. Cada donación contribuye directamente a nuestros proyectos de energía renovable, agricultura sostenible y emprendimiento en Haití.",
      impactTitle: "El Impacto de Tu Donación",
      impacts: [
        { icon: Sun, amount: "$50 USD", desc: "Proporciona un kit solar para una familia" },
        { icon: Leaf, amount: "$100 USD", desc: "Planta 50 árboles frutales" },
        { icon: Users, amount: "$250 USD", desc: "Formación empresarial para 5 jóvenes" },
        { icon: Gift, amount: "$500 USD", desc: "Equipo agrícola para un agricultor" }
      ],
      selectAmount: "Selecciona un monto",
      amounts: [25, 50, 100, 250, 500, 1000],
      customAmount: "Otro monto",
      customPlaceholder: "Ingresa tu monto",
      donorInfo: "Tu información",
      nameLabel: "Tu nombre",
      namePlaceholder: "Juan Pérez",
      emailLabel: "Correo (para recibo fiscal)",
      emailPlaceholder: "tu@correo.com",
      anonymous: "Hacer una donación anónima",
      useAccount: "Usar mi cuenta",
      totalDonation: "Tu donación",
      donateBtn: "Donar",
      processing: "Redirigiendo al pago...",
      secureNote: "Pago 100% seguro",
      paymentMethods: "Métodos de pago aceptados",
      taxNote: "Se enviará un recibo fiscal a tu correo",
      thankYou: "¡Gracias por tu generosidad!",
      whyDonate: "¿Por qué Donar?",
      reasons: [
        "100% de las donaciones van directamente a proyectos en Haití",
        "Organización sin fines de lucro registrada",
        "Informes de impacto transparentes",
        "Recibo fiscal disponible"
      ],
      contactNote: "¿Preguntas sobre donaciones?",
      successMessage: "¡Gracias por tu donación! Recibirás un recibo por correo.",
      canceledMessage: "Donación cancelada. Puedes intentarlo de nuevo cuando quieras.",
      minAmountError: "El monto mínimo es $5 USD",
      loginPrompt: "Inicia sesión para usar tu información",
      orContinueAs: "o continúa como invitado"
    },
    pt: {
      title: "Fazer uma Doação",
      subtitle: "Apoie o desenvolvimento sustentável no Haiti",
      intro: "Sua generosidade faz a diferença. Cada doação contribui diretamente para nossos projetos de energia renovável, agricultura sustentável e empreendedorismo no Haiti.",
      impactTitle: "O Impacto da Sua Doação",
      impacts: [
        { icon: Sun, amount: "$50 USD", desc: "Fornece um kit solar para uma família" },
        { icon: Leaf, amount: "$100 USD", desc: "Planta 50 árvores frutíferas" },
        { icon: Users, amount: "$250 USD", desc: "Treinamento empresarial para 5 jovens" },
        { icon: Gift, amount: "$500 USD", desc: "Equipamento agrícola para um agricultor" }
      ],
      selectAmount: "Selecione um valor",
      amounts: [25, 50, 100, 250, 500, 1000],
      customAmount: "Outro valor",
      customPlaceholder: "Digite seu valor",
      donorInfo: "Suas informações",
      nameLabel: "Seu nome",
      namePlaceholder: "João Silva",
      emailLabel: "Email (para recibo fiscal)",
      emailPlaceholder: "seu@email.com",
      anonymous: "Fazer uma doação anônima",
      useAccount: "Usar minha conta",
      totalDonation: "Sua doação",
      donateBtn: "Doar",
      processing: "Redirecionando para pagamento...",
      secureNote: "Pagamento 100% seguro",
      paymentMethods: "Métodos de pagamento aceitos",
      taxNote: "Um recibo fiscal será enviado para seu email",
      thankYou: "Obrigado pela sua generosidade!",
      whyDonate: "Por que Doar?",
      reasons: [
        "100% das doações vão diretamente para projetos no Haiti",
        "Organização sem fins lucrativos registrada",
        "Relatórios de impacto transparentes",
        "Recibo fiscal disponível"
      ],
      contactNote: "Perguntas sobre doações?",
      successMessage: "Obrigado pela sua doação! Você receberá um recibo por email.",
      canceledMessage: "Doação cancelada. Você pode tentar novamente quando quiser.",
      minAmountError: "O valor mínimo é $5 USD",
      loginPrompt: "Faça login para usar suas informações",
      orContinueAs: "ou continue como convidado"
    }
  };

  const t = content[i18n.language] || content.fr;

  const getFinalAmount = () => {
    if (customAmount && !isNaN(parseFloat(customAmount))) {
      return parseFloat(customAmount);
    }
    return selectedAmount || 0;
  };

  // Mobile payment translations
  const mobilePaymentTexts = {
    fr: {
      title: 'Paiement Mobile',
      instructions: 'Instructions de paiement',
      step1: 'Ouvrez votre application',
      step2: 'Envoyez le montant exact au numéro ci-dessous',
      step3: 'Dans la note/référence, écrivez votre EMAIL',
      step4: 'Une fois le paiement confirmé, nous vous enverrons un reçu par email ou WhatsApp sous 24h',
      amount: 'Montant à envoyer',
      amountUSD: 'Montant en USD',
      amountHTG: 'Montant en Gourdes (HTG)',
      amountCAD: 'Montant en CAD (Interac)',
      phoneNumber: 'Numéro de téléphone',
      emailAddress: 'Adresse email',
      reference: 'Référence (votre email)',
      important: 'IMPORTANT',
      importantNote: 'Après avoir effectué le paiement, veuillez nous contacter sur WhatsApp avec la capture d\'écran de la transaction pour accélérer le traitement.',
      whatsappContact: 'Contacter sur WhatsApp',
      close: 'Fermer',
      copyNumber: 'Copier le numéro',
      copied: 'Copié!',
      confirmRequest: 'Recevoir les instructions par email',
      confirming: 'Envoi en cours...',
      requestConfirmed: '✅ Demande enregistrée !',
      emailSent: 'Un email de confirmation avec les instructions vous a été envoyé.',
      // Moncash/Natcash specific
      moncashNote: 'Le montant sera converti en Gourdes Haïtiennes (HTG)',
      natcashNote: 'Le montant sera converti en Gourdes Haïtiennes (HTG)',
      haitiOnly: 'Haïti seulement',
      // PayPal specific
      paypalTitle: 'Paiement PayPal',
      paypalStep1: 'Connectez-vous à votre compte PayPal',
      paypalStep2: 'Cliquez sur "Envoyer de l\'argent"',
      paypalStep3: 'Envoyez le montant exact à l\'adresse ci-dessous',
      paypalStep4: 'Ajoutez votre email en note pour identification',
      // Interac specific
      interacTitle: 'Virement Interac',
      interacStep1: 'Accédez à votre banque en ligne',
      interacStep2: 'Sélectionnez "Virement Interac"',
      interacStep3: 'Envoyez le montant exact à l\'adresse ci-dessous',
      interacStep4: 'Ajoutez votre email dans le message',
      interacNote: 'Le montant sera converti en dollars canadiens (CAD)',
      canadaOnly: 'Canada seulement'
    },
    en: {
      title: 'Mobile Payment',
      instructions: 'Payment Instructions',
      step1: 'Open your app',
      step2: 'Send the exact amount to the number below',
      step3: 'In the note/reference, write your EMAIL',
      step4: 'Once payment is confirmed, we will send you a receipt by email or WhatsApp within 24h',
      amount: 'Amount to send',
      amountUSD: 'Amount in USD',
      amountHTG: 'Amount in Gourdes (HTG)',
      amountCAD: 'Amount in CAD (Interac)',
      phoneNumber: 'Phone number',
      emailAddress: 'Email address',
      reference: 'Reference (your email)',
      important: 'IMPORTANT',
      importantNote: 'After making the payment, please contact us on WhatsApp with the transaction screenshot to speed up processing.',
      whatsappContact: 'Contact on WhatsApp',
      close: 'Close',
      copyNumber: 'Copy number',
      copied: 'Copied!',
      confirmRequest: 'Receive instructions by email',
      confirming: 'Sending...',
      requestConfirmed: '✅ Request registered!',
      emailSent: 'A confirmation email with instructions has been sent to you.',
      // Moncash/Natcash specific
      moncashNote: 'Amount will be converted to Haitian Gourdes (HTG)',
      natcashNote: 'Amount will be converted to Haitian Gourdes (HTG)',
      haitiOnly: 'Haiti only',
      // PayPal specific
      paypalTitle: 'PayPal Payment',
      paypalStep1: 'Log in to your PayPal account',
      paypalStep2: 'Click on "Send Money"',
      paypalStep3: 'Send the exact amount to the address below',
      paypalStep4: 'Add your email in the note for identification',
      // Interac specific
      interacTitle: 'Interac e-Transfer',
      interacStep1: 'Access your online banking',
      interacStep2: 'Select "Interac e-Transfer"',
      interacStep3: 'Send the exact amount to the address below',
      interacStep4: 'Add your email in the message',
      interacNote: 'Amount will be converted to Canadian dollars (CAD)',
      canadaOnly: 'Canada only'
    },
    ht: {
      title: 'Peman Mobil',
      instructions: 'Enstriksyon pou peman',
      step1: 'Ouvri aplikasyon ou',
      step2: 'Voye kantite egzak la nan nimewo ki anba a',
      step3: 'Nan nòt/referans, ekri EMAIL ou',
      step4: 'Yon fwa peman an konfime, nou pral voye yon resi pa imèl oswa WhatsApp nan 24è',
      amount: 'Kantite pou voye',
      amountUSD: 'Kantite an USD',
      amountHTG: 'Kantite an Goud (HTG)',
      amountCAD: 'Kantite an CAD (Interac)',
      phoneNumber: 'Nimewo telefòn',
      emailAddress: 'Adrès imèl',
      reference: 'Referans (imèl ou)',
      important: 'ENPÒTAN',
      importantNote: 'Apre ou fin fè peman an, tanpri kontakte nou sou WhatsApp ak foto tranzaksyon an pou akselere tretman an.',
      whatsappContact: 'Kontakte sou WhatsApp',
      close: 'Fèmen',
      copyNumber: 'Kopye nimewo',
      copied: 'Kopye!',
      confirmRequest: 'Resevwa enstriksyon pa imèl',
      confirming: 'Ap voye...',
      requestConfirmed: '✅ Demann anrejistre!',
      emailSent: 'Yon imèl konfimasyon ak enstriksyon voye ba ou.',
      // Moncash/Natcash specific
      moncashNote: 'Kantite a ap konvèti an Goud Ayisyen (HTG)',
      natcashNote: 'Kantite a ap konvèti an Goud Ayisyen (HTG)',
      haitiOnly: 'Ayiti sèlman',
      // PayPal specific
      paypalTitle: 'Peman PayPal',
      paypalStep1: 'Konekte nan kont PayPal ou',
      paypalStep2: 'Klike sou "Voye Lajan"',
      paypalStep3: 'Voye kantite egzak la nan adrès ki anba a',
      paypalStep4: 'Ajoute imèl ou nan nòt pou idantifikasyon',
      // Interac specific
      interacTitle: 'Transfè Interac',
      interacStep1: 'Ale nan bank anliy ou',
      interacStep2: 'Chwazi "Transfè Interac"',
      interacStep3: 'Voye kantite egzak la nan adrès ki anba a',
      interacStep4: 'Ajoute imèl ou nan mesaj la',
      interacNote: 'Kantite a ap konvèti an dola Kanadyen (CAD)',
      canadaOnly: 'Kanada sèlman'
    },
    es: {
      title: 'Pago Móvil',
      instructions: 'Instrucciones de pago',
      step1: 'Abra su aplicación',
      step2: 'Envíe el monto exacto al número siguiente',
      step3: 'En la nota/referencia, escriba su EMAIL',
      step4: 'Una vez confirmado el pago, le enviaremos un recibo por email o WhatsApp en 24h',
      amount: 'Monto a enviar',
      amountUSD: 'Monto en USD',
      amountHTG: 'Monto en Gourdes (HTG)',
      amountCAD: 'Monto en CAD (Interac)',
      phoneNumber: 'Número de teléfono',
      emailAddress: 'Dirección de email',
      reference: 'Referencia (su email)',
      important: 'IMPORTANTE',
      importantNote: 'Después de realizar el pago, contáctenos en WhatsApp con la captura de pantalla de la transacción para acelerar el procesamiento.',
      whatsappContact: 'Contactar en WhatsApp',
      close: 'Cerrar',
      copyNumber: 'Copiar número',
      copied: '¡Copiado!',
      confirmRequest: 'Recibir instrucciones por email',
      confirming: 'Enviando...',
      requestConfirmed: '✅ ¡Solicitud registrada!',
      emailSent: 'Se le ha enviado un email de confirmación con las instrucciones.',
      // Moncash/Natcash specific
      moncashNote: 'El monto se convertirá a Gourdes Haitianos (HTG)',
      natcashNote: 'El monto se convertirá a Gourdes Haitianos (HTG)',
      haitiOnly: 'Solo Haití',
      // PayPal specific
      paypalTitle: 'Pago PayPal',
      paypalStep1: 'Inicie sesión en su cuenta PayPal',
      paypalStep2: 'Haga clic en "Enviar dinero"',
      paypalStep3: 'Envíe el monto exacto a la dirección siguiente',
      paypalStep4: 'Agregue su email en la nota para identificación',
      // Interac specific
      interacTitle: 'Transferencia Interac',
      interacStep1: 'Acceda a su banca en línea',
      interacStep2: 'Seleccione "Transferencia Interac"',
      interacStep3: 'Envíe el monto exacto a la dirección siguiente',
      interacStep4: 'Agregue su email en el mensaje',
      interacNote: 'El monto se convertirá a dólares canadienses (CAD)',
      canadaOnly: 'Solo Canadá'
    },
    pt: {
      title: 'Pagamento Móvel',
      instructions: 'Instruções de pagamento',
      step1: 'Abra seu aplicativo',
      step2: 'Envie o valor exato para o número abaixo',
      step3: 'Na nota/referência, escreva seu EMAIL',
      step4: 'Após confirmação do pagamento, enviaremos um recibo por email ou WhatsApp em 24h',
      amount: 'Valor a enviar',
      amountUSD: 'Valor em USD',
      amountHTG: 'Valor em Gourdes (HTG)',
      amountCAD: 'Valor em CAD (Interac)',
      phoneNumber: 'Número de telefone',
      emailAddress: 'Endereço de email',
      reference: 'Referência (seu email)',
      important: 'IMPORTANTE',
      importantNote: 'Após efetuar o pagamento, entre em contato conosco no WhatsApp com a captura de tela da transação para acelerar o processamento.',
      whatsappContact: 'Contatar no WhatsApp',
      close: 'Fechar',
      copyNumber: 'Copiar número',
      copied: 'Copiado!',
      confirmRequest: 'Receber instruções por email',
      confirming: 'Enviando...',
      requestConfirmed: '✅ Solicitação registrada!',
      emailSent: 'Um email de confirmação com as instruções foi enviado para você.',
      // Moncash/Natcash specific
      moncashNote: 'O valor será convertido para Gourdes Haitianos (HTG)',
      natcashNote: 'O valor será convertido para Gourdes Haitianos (HTG)',
      haitiOnly: 'Apenas Haiti',
      // PayPal specific
      paypalTitle: 'Pagamento PayPal',
      paypalStep1: 'Faça login na sua conta PayPal',
      paypalStep2: 'Clique em "Enviar dinheiro"',
      paypalStep3: 'Envie o valor exato para o endereço abaixo',
      paypalStep4: 'Adicione seu email na nota para identificação',
      // Interac specific
      interacTitle: 'Transferência Interac',
      interacStep1: 'Acesse seu banco online',
      interacStep2: 'Selecione "Transferência Interac"',
      interacStep3: 'Envie o valor exato para o endereço abaixo',
      interacStep4: 'Adicione seu email na mensagem',
      interacNote: 'O valor será convertido para dólares canadenses (CAD)',
      canadaOnly: 'Apenas Canadá'
    }
  };

  const mpt = mobilePaymentTexts[i18n.language] || mobilePaymentTexts.fr;

  // HTG conversion rate (approximately 1 USD = 135 HTG)
  const HTG_RATE = 135;
  const convertToHTG = (usdAmount) => Math.round(usdAmount * HTG_RATE).toLocaleString();

  // CAD conversion rate (approximately 1 USD = 1.35 CAD)
  const CAD_RATE = 1.35;
  const convertToCAD = (usdAmount) => (usdAmount * CAD_RATE).toFixed(2);

  const handleDonate = async () => {
    const amount = getFinalAmount();
    if (amount < 5) {
      toast.error(t.minAmountError);
      return;
    }

    // Handle manual payment methods - show modal
    if (['moncash', 'natcash', 'paypal', 'interac'].includes(paymentMethod)) {
      setShowMobilePaymentModal(true);
      setMobilePaymentConfirmed(false);
      return;
    }

    setProcessing(true);
    
    try {
      if (paymentMethod === 'crypto') {
        // Crypto donation via NOWPayments
        const response = await axios.post(`${API_URL}/crypto/donation`, {
          amount: amount,
          pay_currency: selectedCrypto,
          donor_name: donorInfo.anonymous ? null : donorInfo.name,
          donor_email: donorInfo.email || null,
          is_anonymous: donorInfo.anonymous,
          origin_url: window.location.origin
        });
        
        setCryptoPayment(response.data);
        setCryptoStatus('waiting');
        setShowCryptoModal(true);
        setProcessing(false);
        
      } else {
        // Stripe donation
        const response = await fetch(`${API_URL}/donations/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: amount,
            donor_name: donorInfo.anonymous ? null : donorInfo.name,
            donor_email: donorInfo.email || null,
            is_anonymous: donorInfo.anonymous,
            origin_url: window.location.origin,
            language: i18n.language
          }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Payment failed');
        }

        const data = await response.json();
        window.location.href = data.checkout_url;
      }
      
    } catch (error) {
      console.error('Donation error:', error);
      toast.error(error.message || 'Une erreur est survenue. Veuillez réessayer.');
      setProcessing(false);
    }
  };

  // Poll crypto payment status
  useEffect(() => {
    let interval;
    if (cryptoPayment && cryptoStatus !== 'finished' && cryptoStatus !== 'failed') {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API_URL}/crypto/payment/${cryptoPayment.payment_id}/status`);
          setCryptoStatus(response.data.status);
          if (response.data.status === 'finished') {
            toast.success(t.successMessage);
            setTimeout(() => {
              setShowCryptoModal(false);
              setCryptoPayment(null);
            }, 2000);
          } else if (response.data.status === 'failed' || response.data.status === 'expired') {
            toast.error('Paiement expiré ou échoué');
            setCryptoPayment(null);
            setShowCryptoModal(false);
          }
        } catch (error) {
          console.error('Error polling crypto status:', error);
        }
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [cryptoPayment, cryptoStatus, t.successMessage]);

  const handleUseAccount = () => {
    if (user) {
      setDonorInfo({
        name: user.name || '',
        email: user.email || '',
        anonymous: false
      });
    } else {
      navigate('/auth');
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">Vérification du paiement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 px-4 sm:px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-5xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 bg-gradient-to-br from-red-400 to-red-600 rounded-full flex items-center justify-center shadow-xl"
            >
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-base sm:text-lg text-accent font-semibold mb-4">{t.subtitle}</p>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm sm:text-base">{t.intro}</p>
          </div>

          {/* Impact Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-12">
            {t.impacts.map((impact, index) => {
              const Icon = impact.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/90 backdrop-blur-md rounded-2xl p-3 sm:p-4 text-center shadow-lg hover:shadow-xl transition-shadow"
                >
                  <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-secondary mx-auto mb-2" />
                  <p className="font-bold text-primary text-sm sm:text-lg">{impact.amount}</p>
                  <p className="text-xs text-gray-600">{impact.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Donation Form */}
            <div className="lg:col-span-2 bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-6 sm:p-8">
              {/* Amount Selection */}
              <h2 className="text-lg sm:text-xl font-bold text-primary mb-4">{t.selectAmount}</h2>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
                {t.amounts.map((amount) => (
                  <button
                    key={amount}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount('');
                    }}
                    data-testid={`amount-${amount}`}
                    className={`py-3 sm:py-4 px-3 sm:px-6 rounded-xl font-bold text-base sm:text-lg transition-all ${
                      selectedAmount === amount && !customAmount
                        ? 'bg-secondary text-white shadow-lg scale-105'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ${amount}
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-6">
                <Label htmlFor="customAmount" className="text-gray-600">{t.customAmount}</Label>
                <div className="relative mt-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-bold">$</span>
                  <Input
                    id="customAmount"
                    type="number"
                    min="5"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      setSelectedAmount(null);
                    }}
                    className="pl-8 h-12 text-lg"
                    placeholder={t.customPlaceholder}
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="border-t border-gray-200 pt-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-700">{t.donorInfo}</h3>
                  {user ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUseAccount}
                      className="text-sm"
                      data-testid="use-account-btn"
                    >
                      <Wallet className="w-4 h-4 mr-1" />
                      {t.useAccount}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/auth')}
                      className="text-sm text-primary"
                    >
                      {t.loginPrompt}
                    </Button>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="donorName">{t.nameLabel}</Label>
                    <Input
                      id="donorName"
                      value={donorInfo.name}
                      onChange={(e) => setDonorInfo({ ...donorInfo, name: e.target.value })}
                      className="mt-1"
                      placeholder={t.namePlaceholder}
                      disabled={donorInfo.anonymous}
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="donorEmail">{t.emailLabel}</Label>
                    <Input
                      id="donorEmail"
                      type="email"
                      value={donorInfo.email}
                      onChange={(e) => setDonorInfo({ ...donorInfo, email: e.target.value })}
                      className="mt-1"
                      placeholder={t.emailPlaceholder}
                      data-testid="donor-email-input"
                    />
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={donorInfo.anonymous}
                      onChange={(e) => setDonorInfo({ ...donorInfo, anonymous: e.target.checked, name: '' })}
                      className="w-5 h-5 rounded border-gray-300 text-secondary focus:ring-secondary"
                      data-testid="anonymous-checkbox"
                    />
                    <span className="text-gray-600">{t.anonymous}</span>
                  </label>
                </div>
              </div>

              {/* Payment Methods */}
              {/* Payment Method Selection */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-600 mb-3 font-medium">{t.paymentMethods}</p>
                
                {/* Payment method tabs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('stripe')}
                    data-testid="payment-stripe"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'stripe'
                        ? 'bg-primary text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-primary'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs sm:text-sm">Carte</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('moncash')}
                    data-testid="payment-moncash"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'moncash'
                        ? 'bg-[#D32F2F] text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-[#D32F2F]'
                    }`}
                  >
                    <span className="w-5 h-5 mx-auto mb-1 flex items-center justify-center text-xs font-bold">MC</span>
                    <span className="text-xs sm:text-sm">Moncash</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('natcash')}
                    data-testid="payment-natcash"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'natcash'
                        ? 'bg-[#1976D2] text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-[#1976D2]'
                    }`}
                  >
                    <span className="w-5 h-5 mx-auto mb-1 flex items-center justify-center text-xs font-bold">NC</span>
                    <span className="text-xs sm:text-sm">Natcash</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    data-testid="payment-paypal"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'paypal'
                        ? 'bg-[#003087] text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-[#003087]'
                    }`}
                  >
                    <span className="w-5 h-5 mx-auto mb-1 flex items-center justify-center text-xs font-bold">PP</span>
                    <span className="text-xs sm:text-sm">PayPal</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('interac')}
                    data-testid="payment-interac"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'interac'
                        ? 'bg-[#FFCC00] text-black'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-[#FFCC00]'
                    }`}
                  >
                    <span className="w-5 h-5 mx-auto mb-1 flex items-center justify-center text-xs font-bold">e-T</span>
                    <span className="text-xs sm:text-sm">Interac</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('crypto')}
                    data-testid="payment-crypto"
                    className={`py-3 px-3 rounded-xl font-medium transition-all ${
                      paymentMethod === 'crypto'
                        ? 'bg-primary text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-primary'
                    }`}
                  >
                    <Wallet className="w-5 h-5 mx-auto mb-1" />
                    <span className="text-xs sm:text-sm">Crypto</span>
                  </button>
                </div>

                {paymentMethod === 'stripe' && (
                  <div className="flex flex-wrap items-center gap-3">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/ba/Stripe_Logo%2C_revised_2016.svg/512px-Stripe_Logo%2C_revised_2016.svg.png" alt="Stripe" className="h-6" />
                    <span className="text-blue-900 font-bold text-lg tracking-wide">VISA</span>
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Mastercard-logo.svg/1280px-Mastercard-logo.svg.png" alt="Mastercard" className="h-6" />
                    <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/American_Express_logo_%282018%29.svg/1200px-American_Express_logo_%282018%29.svg.png" alt="Amex" className="h-5" />
                  </div>
                )}
                
                {['moncash', 'natcash', 'paypal', 'interac'].includes(paymentMethod) && (
                  <div className={`rounded-lg p-3 text-sm ${
                    paymentMethod === 'paypal' ? 'bg-blue-50 border border-blue-200' :
                    paymentMethod === 'interac' ? 'bg-yellow-50 border border-yellow-200' :
                    'bg-amber-50 border border-amber-200'
                  }`}>
                    <p className={`${
                      paymentMethod === 'paypal' ? 'text-blue-800' :
                      paymentMethod === 'interac' ? 'text-yellow-800' :
                      'text-amber-800'
                    }`}>
                      {paymentMethod === 'paypal' ? '💳 PayPal - ' + (i18n.language === 'fr' ? 'Paiement sécurisé' : 'Secure payment') :
                       paymentMethod === 'interac' ? '🏦 Interac - ' + (i18n.language === 'fr' ? 'Virement Canada' : 'Canada e-Transfer') :
                       `📱 ${paymentMethod === 'moncash' ? 'Moncash' : 'Natcash'} - ${i18n.language === 'en' ? 'Manual payment via mobile' : 'Paiement manuel via mobile'}`}
                    </p>
                  </div>
                )}
                
                {paymentMethod === 'crypto' && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Choisir la crypto:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {availableCryptos.map((crypto) => (
                        <button
                          key={crypto.code}
                          type="button"
                          onClick={() => setSelectedCrypto(crypto.code)}
                          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                            selectedCrypto === crypto.code
                              ? 'bg-primary text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:border-primary'
                          }`}
                        >
                          {crypto.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Total & Submit */}
              <div className="bg-gradient-to-br from-primary to-accent rounded-2xl p-6 text-white">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg">{t.totalDonation}</span>
                  <span className="text-3xl sm:text-4xl font-bold">${getFinalAmount()}</span>
                </div>
                <Button
                  onClick={handleDonate}
                  disabled={processing || getFinalAmount() < 5}
                  className="w-full bg-white text-primary hover:bg-white/90 h-14 rounded-full text-lg font-bold disabled:opacity-50"
                  data-testid="donate-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t.processing}
                    </>
                  ) : (
                    <>
                      <Heart className="w-5 h-5 mr-2" />
                      {t.donateBtn} ${getFinalAmount()}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
                <div className="flex items-center justify-center mt-4 space-x-2 text-white/80 text-sm">
                  <Shield className="w-4 h-4" />
                  <span>{t.secureNote}</span>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Why Donate */}
              <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6">
                <h3 className="font-bold text-primary mb-4">{t.whyDonate}</h3>
                <ul className="space-y-3">
                  {t.reasons.map((reason, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tax Receipt Note */}
              <div className="bg-secondary/10 rounded-2xl p-6 text-center">
                <Gift className="w-10 h-10 text-secondary mx-auto mb-3" />
                <p className="text-sm text-gray-700">{t.taxNote}</p>
              </div>

              {/* Contact */}
              <div className="bg-primary/5 rounded-2xl p-6 text-center">
                <p className="text-sm text-gray-600 mb-2">{t.contactNote}</p>
                <p className="font-semibold text-primary">info@marathon-edenlifeintl.org</p>
                <p className="text-primary">1 866-722-7007</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Crypto Payment Modal */}
      {showCryptoModal && cryptoPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <h3 className="text-xl font-bold text-center mb-4">
              {cryptoStatus === 'waiting' ? 'En attente du paiement...' : 
               cryptoStatus === 'confirming' ? 'Paiement détecté, confirmation...' :
               cryptoStatus === 'finished' ? '✅ Paiement confirmé!' : 'En attente du paiement...'}
            </h3>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">Envoyez exactement ce montant:</p>
              <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                <span className="font-mono font-bold text-lg">{cryptoPayment.pay_amount}</span>
                <span className="text-gray-500 uppercase">{cryptoPayment.pay_currency}</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">À cette adresse:</p>
              <div className="bg-white rounded-lg p-3 border">
                <p className="font-mono text-xs break-all mb-2">{cryptoPayment.pay_address}</p>
                <Button
                  onClick={() => copyToClipboard(cryptoPayment.pay_address)}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? 'Copié!' : 'Copier l\'adresse'}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-center mb-4">
              {cryptoStatus === 'waiting' && (
                <div className="flex items-center text-yellow-600">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  <span>En attente du paiement...</span>
                </div>
              )}
              {cryptoStatus === 'confirming' && (
                <div className="flex items-center text-blue-600">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  <span>Confirmation en cours...</span>
                </div>
              )}
              {cryptoStatus === 'finished' && (
                <div className="flex items-center text-green-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span>Merci pour votre don!</span>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center mb-4">
              💚 Don de ${getFinalAmount()} USD
            </p>

            <Button
              onClick={() => {
                setShowCryptoModal(false);
                setCryptoPayment(null);
              }}
              variant="outline"
              className="w-full"
            >
              Fermer
            </Button>
          </motion.div>
        </div>
      )}

      {/* Manual Payment Modal (Moncash/Natcash/PayPal/Interac) */}
      {showMobilePaymentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto"
            data-testid="mobile-payment-modal"
          >
            {/* Header */}
            <div className={`p-6 text-white bg-gradient-to-r ${getPaymentInfo().color}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">
                    {paymentMethod === 'moncash' ? 'M' : 
                     paymentMethod === 'natcash' ? 'N' : 
                     paymentMethod === 'paypal' ? 'P' : 'e'}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{getPaymentInfo().label}</h3>
                  <p className="text-white/80 text-sm">
                    {paymentMethod === 'paypal' ? 'PayPal Transfer' : 
                     paymentMethod === 'interac' ? 'Interac eTransfer' : 
                     mpt.title}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold mb-3">{mpt.instructions}</h4>
                <ol className="space-y-2 text-sm">
                  {paymentMethod === 'paypal' ? (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.paypalStep1}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.paypalStep2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.paypalStep3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.paypalStep4}</span>
                      </li>
                    </>
                  ) : paymentMethod === 'interac' ? (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.interacStep1}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.interacStep2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.interacStep3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.interacStep4}</span>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.step1} <strong>{getPaymentInfo().label}</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.step2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.step3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.step4}</span>
                      </li>
                    </>
                  )}
                </ol>
              </div>

              {/* Amount */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-700 mb-1">{mpt.amount}</p>
                <p className="text-3xl font-bold text-green-700">${getFinalAmount()} USD</p>
                {['moncash', 'natcash'].includes(paymentMethod) && (
                  <p className="text-lg font-semibold text-green-600 mt-1">
                    ≈ {convertToHTG(getFinalAmount())} HTG
                  </p>
                )}
                {paymentMethod === 'interac' && (
                  <p className="text-lg font-semibold text-green-600 mt-1">
                    ≈ C${convertToCAD(getFinalAmount())} CAD
                  </p>
                )}
                {['moncash', 'natcash'].includes(paymentMethod) && (
                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">
                    🇭🇹 {paymentMethod === 'moncash' ? mpt.moncashNote : mpt.natcashNote} • {mpt.haitiOnly}
                  </p>
                )}
                {paymentMethod === 'interac' && (
                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">
                    🍁 {mpt.interacNote} • {mpt.canadaOnly}
                  </p>
                )}
              </div>

              {/* Contact Info (Phone or Email) */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-2">
                  {getPaymentInfo().type === 'email' ? mpt.emailAddress : mpt.phoneNumber}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`font-mono font-bold text-gray-900 ${getPaymentInfo().type === 'email' ? 'text-lg' : 'text-2xl'}`} data-testid="mobile-payment-number">
                    {getPaymentInfo().value}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getPaymentInfo().value)}
                    className="flex items-center gap-1"
                  >
                    {mobilePaymentCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {mobilePaymentCopied ? mpt.copied : mpt.copyNumber}
                  </Button>
                </div>
              </div>

              {/* Reference */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700 mb-1">{mpt.reference}</p>
                <p className="font-mono font-bold text-blue-900">{donorInfo.email || 'votre@email.com'}</p>
              </div>

              {/* Important Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-bold text-amber-800 mb-1">⚠️ {mpt.important}</p>
                <p className="text-sm text-amber-700">{mpt.importantNote}</p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                    `Bonjour, je viens de faire un don de $${getFinalAmount()} USD via ${getPaymentInfo().label}. Mon email: ${donorInfo.email || '(à compléter)'}. Merci de confirmer la réception.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                  data-testid="whatsapp-contact-btn"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {mpt.whatsappContact}
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 space-y-3">
              {/* Confirmation message if already submitted */}
              {mobilePaymentConfirmed && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-green-700 font-semibold">{mpt.requestConfirmed}</p>
                  <p className="text-green-600 text-sm">{mpt.emailSent}</p>
                </div>
              )}
              
              {/* Submit button */}
              {!mobilePaymentConfirmed && donorInfo.email && (
                <Button
                  onClick={submitMobilePaymentRequest}
                  disabled={mobilePaymentSubmitting}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="confirm-mobile-payment-btn"
                >
                  {mobilePaymentSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {mpt.confirming}
                    </>
                  ) : (
                    <>
                      📧 {mpt.confirmRequest}
                    </>
                  )}
                </Button>
              )}
              
              <Button
                onClick={() => {
                  setShowMobilePaymentModal(false);
                  setMobilePaymentConfirmed(false);
                }}
                variant="outline"
                className="w-full"
                data-testid="close-mobile-modal-btn"
              >
                {mpt.close}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Donate;
